/**
 * Захиалга, борлуулалт — прототипийн `04 Orders.dc.html`.
 *
 * Одоо БОДИТ: `/orders` ба `/reports/sales` модуль хийгдсэн.
 *
 * ⚠️ Прототипийн таб нь хүргэлтийн төлөв байсан («Хүргэлт гарсан»,
 *    «Хүргэгдсэн»). Энэ бол дэлгүүрийн борлуулалт — схемд `OrderStatus`
 *    нь `ACTIVE` / `VOIDED` хоёрхон. Тиймээс табыг бодит төлөв,
 *    төлбөрийн хэлбэрээр сольсон.
 *
 * Захиалга үүсгэх нь `/sales` (касс) дэлгэц дээр. Энд жагсаалт,
 * дэлгэрэнгүй, хүчингүй болгох, мөрийн буцаалт.
 *
 * ⚠️ `orders` / `order_lines` нь өөрчлөгдөшгүй (DB trigger). Засвар
 *    гэж байхгүй: алдаатай бол VOID + шинэ захиалга, буцаалт бол
 *    `returns` мөр (CLAUDE.md дүрэм 2).
 */

import { useEffect, useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import {
  LEGACY_CREDIT_FEE_LABEL, VAT_PCT, formatDate, formatDateTime, formatMnt, formatMntShort, formatQty, formatSerialDisplay,
} from '@boots/domain';
import { api, download, type Envelope } from '../lib/api';
import { useAuth } from '../lib/auth';
import {
  COST_ROLES,
  PAYMENT_LABEL,
  RETURN_DISPOSITION_LABEL,
  type OrderListMeta,
  type ItemPhotoView,
  type OrderRow,
  type SalesReport,
} from '../lib/types';
import { REFUND_METHOD_OPTIONS, RETURN_TYPE_LABEL, defaultRefundMethod } from '../lib/refunds';
import {
  Button, Empty, ErrorNote, Field, Modal, Select, Spinner, Textarea,
} from '../components/ui';
import { DataTable, type Column } from '../components/data-table';
import { SalesFilters } from '../components/reports/sales-filters';
import { GroupBars } from '../components/reports/group-bars';
import { StoreSales } from '../components/reports/store-sales';
import { Section } from '../components/section';
import { salesQuery, useReportFilter } from '../lib/use-report-filter';
import { EmptyState } from '../components/empty-state';
import { StatCard, StatGrid, StatPanel } from '../components/stat-card';


type Tab = 'all' | 'active' | 'voided' | 'returns';

/**
 * Төлбөрийг НЭГ мөрөнд. Хэд хэдэн хэлбэрээр төлсөн бол «Холимог» гэж
 * бичих нь юу ч хэлэхгүй тул задаргааг нь гаргана; зээлийн апп бол
 * аппын нэрээр («LendMN 1,058,888₮»).
 */
function paymentSummary(o: OrderRow): string {
  const parts = o.payments ?? [];
  if (parts.length <= 1) {
    const one = parts[0];
    return one?.provider?.labelMn ?? PAYMENT_LABEL[o.payment] ?? o.payment;
  }
  return parts
    .map((p) => `${p.provider?.labelMn ?? PAYMENT_LABEL[p.method] ?? p.method} ${formatMnt(p.amountMnt)}`)
    .join(' + ');
}

/** Шүүлтүүрийн талбарын нэг хэлбэр — тайлангийн мөртэй ижил. */
const FILTER_FIELD =
  'min-h-11 rounded-full border border-hair bg-panel px-3.5 py-1 text-base text-ink ' +
  'focus:border-accent focus:outline-none sm:min-h-0 sm:text-xs';

const TABS: { key: Tab; label: string }[] = [
  { key: 'all', label: 'Бүгд' },
  { key: 'active', label: 'Идэвхтэй' },
  { key: 'voided', label: 'Хүчингүй' },
  { key: 'returns', label: 'Буцаалт' },
];

interface OrderList {
  data: OrderRow[];
  meta: OrderListMeta;
}

export function OrdersPage() {
  const { can, readOnly, hasPerm } = useAuth();
  const canExport = hasPerm('reports.view') || hasPerm('finance.view');
  const showCost = can(...COST_ROLES);
  const [tab, setTab] = useState<Tab>('all');
  const [params, setParams] = useSearchParams();
  const [openId, setOpenId] = useState<string | null>(null);

  // Кассаас `?open=<id>`-тэй ирвэл шинэ захиалгыг шууд нээнэ
  const openParam = params.get('open');
  useEffect(() => {
    if (openParam) {
      setOpenId(openParam);
      params.delete('open');
      setParams(params, { replace: true });
    }
  }, [openParam, params, setParams]);

  /*
    Хугацаа, салбарын шүүлтүүр — URL-д хадгалагдана (`?range=…&store=…`),
    тайлангийн хуудастай ИЖИЛ хэрэгслээр. Жагсаалт, гол үзүүлэлт,
    салбарын задаргаа ГУРВУУЛАА нэг шүүлтээр явна: нэг нь нөгөөгөөсөө
    өөр хугацаа харуулбал тоонууд хоорондоо зөрөх болно.
  */
  const f = useReportFilter();
  /*
    Нэмэлт шүүлтүүр — захиалгын код / худалдан авагчийн утас, дүнгийн
    хязгаар. `useReportFilter`-ийн хугацаа, салбартай НЭГ URL-д.
  */
  const search = params.get('q') ?? '';
  const minTotal = params.get('min') ?? '';
  const maxTotal = params.get('max') ?? '';
  const setFilter = (kv: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(kv)) {
      if (v) next.set(k, v);
      else next.delete(k);
    }
    setParams(next, { replace: true });
  };
  const scope = f.range ? salesQuery(f.range.from, f.range.to, f.storeId) : '';
  const listQuery = [
    scope,
    search ? `q=${encodeURIComponent(search)}` : '',
    minTotal ? `minTotal=${encodeURIComponent(minTotal)}` : '',
    maxTotal ? `maxTotal=${encodeURIComponent(maxTotal)}` : '',
  ]
    .filter(Boolean)
    .join('&');

  const orders = useQuery({
    queryKey: ['orders', listQuery],
    queryFn: () => api<OrderList>(`/orders?limit=100&${listQuery}`),
    enabled: scope !== '',
    placeholderData: keepPreviousData,
  });

  /*
    «Ямар үнийн гутал», «аль гарагт» их зарагддаг вэ — хоёр задаргаа.
    Хугацаа, салбарын шүүлт нь дээрх KPI-тай ИЖИЛ тул тоонууд
    хоорондоо зөрөхгүй.

    ⚠️ Жагсаалтын нэмэлт шүүлт (код, утас, дүн) ЭНД ОРОХГҮЙ: эдгээр нь
       нэгтгэсэн дүр зураг — «энэ хугацаанд юу их зарагдав» гэдгийг
       нэг захиалга хайж байхад ч харуулна.
  */
  const bands = useQuery({
    queryKey: ['sales-report', 'orders-band', scope],
    queryFn: () => api<Envelope<SalesReport>>(`/reports/sales?groupBy=priceBand&${scope}`),
    enabled: scope !== '',
    placeholderData: keepPreviousData,
  });
  const weekdays = useQuery({
    queryKey: ['sales-report', 'orders-weekday', scope],
    queryFn: () => api<Envelope<SalesReport>>(`/reports/sales?groupBy=weekday&${scope}`),
    enabled: scope !== '',
    placeholderData: keepPreviousData,
  });

  // Ашиг нь зарах мөчид хөлдсөн өртгөөс. `groupBy=store` — доорх
  // «Салбараар» задаргаа ба дээрх дүнг НЭГ хүсэлтээс авна.
  const sales = useQuery({
    queryKey: ['sales-report', 'orders-page', scope],
    queryFn: () => api<Envelope<SalesReport>>(`/reports/sales?groupBy=store&${scope}`),
    enabled: scope !== '',
    placeholderData: keepPreviousData,
  });

  const err = orders.error ?? sales.error;
  if (err) return <ErrorNote>{(err as Error).message}</ErrorNote>;
  // Хугацаа гараар сонгож байхад (нэг огноо дутуу) хүсэлт явуулахгүй
  if (!f.range) {
    return (
      <div className="flex flex-col gap-3">
        <SalesFilters f={f} showExport={false} />
        <p className="text-[12.5px] text-ink-3">Эхлэх, дуусах огноог сонгоно уу</p>
      </div>
    );
  }
  if (!orders.data || !sales.data) return <Spinner />;

  // Энэ цэгээс хойш муж БАТАЛГААТАЙ — дээр нь  бий
  const range = f.range;
  const rows = orders.data.data;
  const totals = sales.data.data.totals;

  const filtered = rows.filter((o) =>
    tab === 'active' ? o.status === 'ACTIVE' : tab === 'voided' ? o.status === 'VOIDED' : true,
  );

  const counts = {
    all: rows.length,
    active: rows.filter((o) => o.status === 'ACTIVE').length,
    voided: rows.filter((o) => o.status === 'VOIDED').length,
    returns: undefined,
  } as Record<Tab, number | undefined>;

  const avgOrder =
    orders.data.meta.totals.activeOrders > 0
      ? Number(orders.data.meta.totals.totalMnt) / orders.data.meta.totals.activeOrders
      : 0;

  return (
    <div className={`flex flex-col gap-3 ${orders.isPlaceholderData ? 'opacity-60' : ''}`}>
      {/* ─────────── Шүүлтүүр — жагсаалт, KPI, задаргаа гурвуулаа ─────────── */}
      <div className="flex flex-col gap-2.5 rounded-card border border-hair bg-panel px-card py-3">
        <SalesFilters f={f} showExport={false} />

        <div className="flex min-w-0 flex-wrap items-center gap-2 border-t border-hair pt-2.5">
          {/*
            Хайлт — захиалгын код ЭСВЭЛ худалдан авагчийн утас. Утсыг
            сервер нормчилно: «99112233», «+97699112233» хоёул олдоно.
          */}
          <input
            type="search"
            value={search}
            onChange={(e) => setFilter({ q: e.target.value || null })}
            placeholder="Захиалгын код эсвэл утас"
            aria-label="Захиалгын код эсвэл утсаар хайх"
            className={`${FILTER_FIELD} min-w-0 flex-1 sm:max-w-[240px] ${
              search ? 'border-accent-700 text-accent-700' : ''
            }`}
          />

          <span className="flex min-w-0 items-center gap-1.5">
            <input
              inputMode="numeric"
              value={minTotal}
              onChange={(e) => setFilter({ min: e.target.value.replace(/\D/g, '') || null })}
              placeholder="Дүн, доод"
              aria-label="Хамгийн бага дүн"
              className={`${FILTER_FIELD} num w-[110px] ${
                minTotal ? 'border-accent-700 text-accent-700' : ''
              }`}
            />
            <span aria-hidden className="text-ink-3">–</span>
            <input
              inputMode="numeric"
              value={maxTotal}
              onChange={(e) => setFilter({ max: e.target.value.replace(/\D/g, '') || null })}
              placeholder="дээд"
              aria-label="Хамгийн их дүн"
              className={`${FILTER_FIELD} num w-[100px] ${
                maxTotal ? 'border-accent-700 text-accent-700' : ''
              }`}
            />
          </span>

          {search || minTotal || maxTotal ? (
            <button
              type="button"
              onClick={() => setFilter({ q: null, min: null, max: null })}
              className="text-[12.5px] text-accent-700 hover:underline"
            >
              Цэвэрлэх
            </button>
          ) : null}

          {/*
            CSV — зарагдсан гутал бүр нэг мөр, ОДООГИЙН шүүлтээр.
            ⚠️ Бөөнөөр татах нь дэлгэцээр харахаас эмзэг тул тусдаа
               эрхтэй (шийдвэр 2026-09-18) — эрхгүй хүнд товч гарахгүй.
          */}
          {canExport ? (
            <button
              type="button"
              onClick={() =>
                void download(
                  `/reports/sales/export?${salesQuery(range.from, range.to, f.storeId)}`,
                  'borluulalt.csv',
                )
              }
              className="ml-auto inline-flex min-h-11 items-center rounded-full border border-hair
                px-3.5 py-1 text-[12.5px] whitespace-nowrap transition hover:bg-well sm:min-h-0"
            >
              CSV татах
            </button>
          ) : null}
        </div>
      </div>

      {/* ─────────── KPI ─────────── */}
      <StatPanel>
      <StatGrid>
        <StatCard
          label="Борлуулалт"
          icon="receipt"
          value={formatMntShort(totals.revenueMnt)}
          delta={{ text: `${formatQty(totals.units)} гутал · цэвэр дүн` }}
        />
        <StatCard
          label="Ашиг"
          icon="chart"
          value={showCost ? formatMntShort(totals.profitMnt) : '•••'}
          delta={{
            text:
              showCost && totals.uncostedUnits > 0
                ? `${formatQty(totals.uncostedUnits)} гутал өртөггүй — ашигт ороогүй`
                : showCost && totals.marginPct
                  ? `${totals.marginPct}% маржин`
                  : 'нийт өртөг суурьтай',
          }}
          pending={!showCost}
        />
        <StatCard
          label="Дундаж захиалга"
          icon="coins"
          value={formatMntShort(String(Math.round(avgOrder)))}
          delta={{ text: `${formatQty(orders.data.meta.totals.activeOrders)} идэвхтэй захиалга` }}
        />
        {/* «Урамшуулал» нуугдсан (2026-10-05, №16) — `sales-block.tsx`-ийн тайлбарыг үз */}
      </StatGrid>
      </StatPanel>

      {/*
        Салбар бүр хэдийг зарсан бэ. Дүн нь дээрх KPI-тай ИЖИЛ хүсэлтээс
        (`groupBy=store`) гардаг тул хоорондоо хэзээ ч зөрөхгүй.
        Салбар дээр дарвал жагсаалт нь тэр салбараар шүүгдэнэ.
      */}
      {/*
        «Хэдэн төгрөгийн гутал хамгийн их зарагддаг вэ» ба «долоо хоногийн
        аль өдөр» — хоёулаа ИЖИЛ шүүлтээс. Хажуу хажуугаа: хуудсыг
        уртасгахгүй, хоёулаа нэг харцанд багтана.
      */}
      <div className="grid gap-3 lg:grid-cols-2">
        <Section title="Үнийн бүсээр" note="аль үнийн гутал их зарагддаг вэ">
          <GroupBars
            groups={bands.data?.data.groups ?? []}
            pending={bands.isPending}
            empty="Энэ хугацаанд борлуулалт алга"
          />
        </Section>
        <Section title="Гарагаар" note="долоо хоногийн аль өдөр их зардаг вэ">
          <GroupBars
            groups={weekdays.data?.data.groups ?? []}
            pending={weekdays.isPending}
            empty="Энэ хугацаанд борлуулалт алга"
            keepOrder
          />
        </Section>
      </div>

      <Section title="Салбараар" note="буцаагдсан, хүчингүй болсныг хассан цэвэр дүн">
        <StoreSales
          groups={sales.data.data.groups}
          activeId={f.storeId}
          onPick={(storeId) => f.set({ storeId })}
        />
      </Section>

      {/* ─────────── Таб ─────────── */}
      <div className="flex flex-wrap items-center gap-2.5">
        <div className="flex flex-wrap gap-1 rounded-full bg-shell p-[3px]">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={`rounded-full px-3.5 py-1.5 text-[12.5px] transition ${
                tab === t.key ? 'bg-accent-fill text-white' : 'text-ink-2 hover:text-ink'
              }`}
            >
              {t.label}
              {counts[t.key] === undefined ? null : (
                <span className="num opacity-70"> {formatQty(counts[t.key])}</span>
              )}
            </button>
          ))}
        </div>
        {can('SALESPERSON', 'STORE_MANAGER', 'ADMIN') && !readOnly ? (
          <Link
            to="/sales"
            className="ml-auto inline-flex items-center gap-2 rounded-full bg-accent px-4 py-2
              text-sm font-medium text-white transition hover:bg-accent-fill-hover active:translate-y-[0.5px]"
          >
            ＋ Шинэ борлуулалт
          </Link>
        ) : (
          <span className="ml-auto text-[11.5px] text-ink-3">
            Борлуулалт бүртгэх эрх танд байхгүй — зөвхөн харна
          </span>
        )}
      </div>

      {tab === 'returns' ? <ReturnsTable /> : (
      /* ─────────── Хүснэгт ─────────── */
      <section className="overflow-hidden rounded-table sm:border sm:border-hair sm:bg-panel">
        <DataTable
          rows={filtered}
          columns={orderColumns()}
          rowKey={(o) => o.id}
          onRowClick={(o) => setOpenId(o.id)}
          empty={
            <EmptyState
              title="Захиалга алга"
              description="Энэ шүүлтэнд тохирох борлуулалт бүртгэгдээгүй байна"
            />
          }
        />

        <div className="flex items-center gap-3 px-card py-3.5 sm:border-t sm:border-hair">
          <span className="num text-[12.5px] text-ink-2">
            Нийт дүн: {formatMnt(orders.data.meta.totals.totalMnt)}
          </span>
          <span className="ml-auto text-[12.5px] text-ink-3">
            {formatQty(filtered.length)} / {formatQty(orders.data.meta.total)}
          </span>
        </div>
      </section>
      )}

      {openId ? <OrderDetail id={openId} onClose={() => setOpenId(null)} /> : null}
    </div>
  );
}

// ------------------------------------------------------------ Буцаалт
interface ReturnRow {
  id: string;
  type: 'REFUND' | 'EXCHANGE';
  disposition: string;
  /** Худалдан авагчид бодитоор буцаасан мөнгө */
  refundMnt: string;
  refundMethod: string | null;
  /** Солилцооны шинэ баримт */
  exchangeOrder: { id: string; code: string } | null;
  returnedAt: string;
  notes: string | null;
  reason: { labelMn: string } | null;
  processedBy: { fullName: string } | null;
  orderLine: {
    id: string;
    snapshot: { serial: string; size: number; brand: string; modelName: string };
    /** Анхны баримт */
    order: { id: string; code: string };
  } | null;
}

interface ReturnTotals { count: number; refundMnt: string }

function ReturnsTable() {
  const q = useQuery({
    queryKey: ['returns'],
    queryFn: () => api<{
      data: ReturnRow[];
      meta: { count: number; total: number; refundMnt: string; byType: Record<'REFUND' | 'EXCHANGE', ReturnTotals> };
    }>('/returns?limit=100'),
  });

  if (q.isPending) return <Spinner />;
  if (q.error) return <ErrorNote>{(q.error as Error).message}</ErrorNote>;

  const rows = q.data.data;
  // Сервер БҮХ мөрөөр нэмсэн — харагдаж буй 100-аар биш
  const { byType } = q.data.meta;

  return (
    <section className="overflow-hidden rounded-table sm:border sm:border-hair sm:bg-panel">
      <DataTable
        rows={rows}
        columns={returnColumns()}
        rowKey={(r) => r.id}
        empty={<Empty>Буцаалт бүртгэгдээгүй</Empty>}
      />
      <div className="flex items-center gap-3 px-card py-3.5 sm:border-t sm:border-hair">
        <span className="num text-[12.5px] text-ink-2">
          Бүтэн буцаалт {formatQty(byType.REFUND.count)} · {formatMnt(byType.REFUND.refundMnt)}
          {' · '}Солилцоо {formatQty(byType.EXCHANGE.count)} · зөрүү буцаасан {formatMnt(byType.EXCHANGE.refundMnt)}
        </span>
        <span className="ml-auto text-[12.5px] text-ink-3">
          {formatQty(rows.length)} / {formatQty(q.data.meta.total)}
        </span>
      </div>
    </section>
  );
}

// ------------------------------------------------------------ Дэлгэрэнгүй
interface OrderLineDetail {
  id: string;
  itemId: string;
  priceMnt: string;
  discountMnt: string;
  lineTotalMnt: string;
  commissionMnt: string;
  warrantyEndsAt: string | null;
  snapshot: { serial: string; size: number; brand: string; modelName: string };
  /**
   * Гутлын ОДООГИЙН хөрөг зураг.
   *
   * ⚠️ `snapshot.photoKeys` БИШ: тэдгээр объект баталгаа дууссанаас
   *    хойш 6 сарын дараа устдаг (`photo:purge`). Энэ нь одоо байгааг
   *    харуулна — байхгүй бол зүгээр л хоосон.
   */
  photo?: ItemPhotoView | null;
  return?: { id: string; disposition: string; returnedAt: string } | null;
}

interface OrderDetailData extends OrderRow {
  subtotalMnt: string;
  externalPosRef: string | null;
  voidedAt: string | null;
  lines: OrderLineDetail[];
}

function OrderDetail({ id, onClose }: { id: string; onClose: () => void }) {
  const { can, readOnly } = useAuth();
  const qc = useQueryClient();
  const [voiding, setVoiding] = useState(false);
  const [returning, setReturning] = useState<OrderLineDetail | null>(null);

  const q = useQuery({
    queryKey: ['order', id],
    queryFn: () => api<Envelope<OrderDetailData>>(`/orders/${id}`),
  });

  const o = q.data?.data;
  const canVoid = can('STORE_MANAGER', 'ADMIN') && !readOnly;
  const canReturn = can('SALESPERSON', 'STORE_MANAGER', 'ADMIN') && !readOnly;

  const refresh = async () => {
    await qc.invalidateQueries({ queryKey: ['order', id] });
    await qc.invalidateQueries({ queryKey: ['orders'] });
  };

  return (
    <Modal
      wide
      title={o ? `Захиалга ${o.code}` : 'Захиалга'}
      subtitle={o ? `${o.store.name} · ${formatDateTime(o.soldAt)} · ${paymentSummary(o)}` : undefined}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Хаах</Button>
          {o?.status === 'ACTIVE' && canVoid ? (
            <Button variant="danger" onClick={() => setVoiding(true)}>Хүчингүй болгох</Button>
          ) : null}
        </>
      }
    >
      {q.isPending ? <Spinner /> : q.error ? (
        <ErrorNote>{(q.error as Error).message}</ErrorNote>
      ) : o ? (
        <>
          {o.status === 'VOIDED' ? (
            <ErrorNote>
              Хүчингүй болгосон{o.voidedAt ? ` · ${formatDateTime(o.voidedAt)}` : ''}
              {o.voidReason ? ` · ${o.voidReason}` : ''}
            </ErrorNote>
          ) : null}

          <div className="flex flex-wrap gap-4 text-[12.5px] text-ink-2">
            <span>Худалдагч: {o.soldBy.fullName}</span>
            <span>Худалдан авагч: {o.customer?.name ?? o.customer?.phone ?? '—'}</span>
            {o.promotion ? <span>Урамшуулал: {o.promotion.labelMn}</span> : null}
            {o.externalPosRef ? <span className="num">POS: {o.externalPosRef}</span> : null}
          </div>

          <div className="table-pin rounded-card border border-hair">
            <table className="w-full border-collapse text-[13px]">
              <thead className="bg-well-2">
                <tr>
                  <Th>Серийн дугаар</Th>
                  <Th>Загвар</Th>
                  <Th num>Размер</Th>
                  <Th num>Үнэ</Th>
                  <Th num>Хөнгөлөлт</Th>
                  <Th num>Дүн</Th>
                  <Th>Баталгаа</Th>
                  <Th> </Th>
                </tr>
              </thead>
              <tbody>
                {o.lines.map((l) => (
                  <tr key={l.id} className="transition hover:bg-row-hover">
                    <Td>
                      {/*
                        Зураг нь «аль гутал байсан бэ» гэдгийг ХАРУУЛНА —
                        загварын нэр бүгд «Ерөнхий» тул цорын ганц
                        таних тэмдэг. Дарвал гутлын хуудас руу.
                      */}
                      <span className="flex items-center gap-2">
                        {l.photo?.thumbUrl ?? l.photo?.mediumUrl ? (
                          <img
                            src={l.photo.thumbUrl ?? l.photo.mediumUrl ?? ''}
                            alt=""
                            loading="lazy"
                            className="size-9 shrink-0 rounded-well border border-hair object-cover"
                          />
                        ) : null}
                        <Link to={`/items/${l.itemId}`} className="num hover:underline">
                          {formatSerialDisplay(l.snapshot.serial)}
                        </Link>
                      </span>
                    </Td>
                    <Td muted>{l.snapshot.brand} · {l.snapshot.modelName}</Td>
                    <Td num>{l.snapshot.size}</Td>
                    <Td num>{formatMnt(l.priceMnt)}</Td>
                    <Td num>{formatMnt(l.discountMnt)}</Td>
                    <Td num><strong>{formatMnt(l.lineTotalMnt)}</strong></Td>
                    <Td muted>{l.warrantyEndsAt ? formatDate(l.warrantyEndsAt) : '—'}</Td>
                    <td className="border-b border-hair/70 px-3 py-2 text-right">
                      {l.return ? (
                        <span className="text-[11px] text-ink-3">Буцаагдсан</span>
                      ) : o.status === 'ACTIVE' && canReturn ? (
                        <button
                          type="button"
                          onClick={() => setReturning(l)}
                          className="inline-flex min-h-11 items-center rounded-full border border-hair px-2.5 py-1 text-[11px] hover:bg-well sm:min-h-0"
                        >
                          Буцаах
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {o.lines.length === 0 ? <Empty>Мөр алга</Empty> : null}
          </div>

          <dl className="ml-auto flex w-full max-w-[300px] flex-col gap-1.5 text-sm">
            <SumLine label="Дэд дүн" value={formatMnt(o.subtotalMnt)} />
            <SumLine label="Хөнгөлөлт" value={`−${formatMnt(o.discountMnt)}`} />
            {/* Нэмэгдэл нь ОРЛОГО БИШ — байвал л мөр гарна */}
            {Number(o.creditFeeMnt) > 0 ? (
              <SumLine label={LEGACY_CREDIT_FEE_LABEL} value={`+${formatMnt(o.creditFeeMnt)}`} />
            ) : null}
            {Number(o.vatMnt) > 0 ? (
              <SumLine label={`И-баримт (НӨАТ) +${VAT_PCT}%`} value={`+${formatMnt(o.vatMnt)}`} />
            ) : null}
            <div className="flex items-baseline justify-between gap-4 border-t border-hair pt-1.5">
              <dt className="font-medium">Нийт</dt>
              <dd className="num text-[17px] font-semibold">{formatMnt(o.totalMnt)}</dd>
            </div>
          </dl>
        </>
      ) : null}

      {voiding && o ? (
        <VoidOrder id={o.id} code={o.code} onClose={() => setVoiding(false)} onDone={async () => { setVoiding(false); await refresh(); }} />
      ) : null}

      {returning ? (
        <ReturnLine
          line={returning}
          payment={o?.payment ?? 'CASH'}
          onClose={() => setReturning(null)}
          onDone={async () => { setReturning(null); await refresh(); }}
        />
      ) : null}
    </Modal>
  );
}

function VoidOrder({
  id, code, onClose, onDone,
}: { id: string; code: string; onClose: () => void; onDone: () => void }) {
  const [reason, setReason] = useState('');
  const run = useMutation({
    mutationFn: () => api(`/orders/${id}/void`, { method: 'POST', body: { reason: reason.trim() } }),
    onSuccess: onDone,
  });

  return (
    <Modal
      title={`${code} — хүчингүй болгох`}
      subtitle="Гутал нөөцөд буцаж орно. Устгал гэж байхгүй."
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Болих</Button>
          <Button variant="danger" onClick={() => run.mutate()} disabled={reason.trim().length < 3 || run.isPending}>
            {run.isPending ? 'Болгож байна…' : 'Хүчингүй болгох'}
          </Button>
        </>
      }
    >
      <Field label="Шалтгаан (заавал)">
        <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} />
      </Field>
      {run.error ? <ErrorNote>{(run.error as Error).message}</ErrorNote> : null}
    </Modal>
  );
}

function ReturnLine({
  line, payment, onClose, onDone,
}: { line: OrderLineDetail; payment: string; onClose: () => void; onDone: () => void }) {
  const [reasonLookupId, setReason] = useState('');
  const [disposition, setDisposition] = useState('RESTOCKED');
  const [refundMethod, setRefundMethod] = useState<string>(defaultRefundMethod(payment));
  const [notes, setNotes] = useState('');

  const eligibility = useQuery({
    queryKey: ['return-eligibility', line.id],
    queryFn: () => api<Envelope<{ eligible: boolean; reasons: string[]; daysSinceSale: number; maxRefundMnt: string }>>(
      `/order-lines/${line.id}/return-eligibility`,
    ),
  });

  const reasons = useQuery({
    queryKey: ['lookups', 'RETURN_REASON'],
    queryFn: () => api<Envelope<{ id: string; labelMn: string }[]>>('/ref/lookups?category=RETURN_REASON&active=true'),
  });

  const run = useMutation({
    mutationFn: () =>
      api(`/order-lines/${line.id}/return`, {
        method: 'POST',
        body: {
          reasonLookupId,
          disposition,
          refundMethod,
          ...(notes.trim() ? { notes: notes.trim() } : {}),
        },
      }),
    onSuccess: onDone,
  });

  const e = eligibility.data?.data;

  return (
    <Modal
      title="Бүтэн буцаалт"
      subtitle={`${formatSerialDisplay(line.snapshot.serial)} · ${line.snapshot.modelName}`}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Болих</Button>
          <Button onClick={() => run.mutate()} disabled={!reasonLookupId || !e?.eligible || run.isPending}>
            {run.isPending ? 'Бүртгэж байна…' : 'Буцаалт бүртгэх'}
          </Button>
        </>
      }
    >
      {eligibility.isPending ? <Spinner /> : e && !e.eligible ? (
        <ErrorNote>Буцаах боломжгүй: {e.reasons.join(', ')}</ErrorNote>
      ) : e ? (
        <p className="text-[12.5px] text-ink-2">
          Зарагдсанаас хойш <span className="num">{formatQty(e.daysSinceSale)}</span> хоног ·
          дээд буцаалт <span className="num">{formatMnt(e.maxRefundMnt)}</span>
        </p>
      ) : null}

      <Field label="Шалтгаан">
        <Select
          value={reasonLookupId}
          onChange={setReason}
          placeholder="Сонгох…"
          options={(reasons.data?.data ?? []).map((r) => ({ value: r.id, label: r.labelMn }))}
        />
      </Field>
      <Field label="Гутлыг яах вэ">
        <Select
          value={disposition}
          onChange={setDisposition}
          options={[
            { value: 'RESTOCKED', label: 'Нөөцөд буцаах' },
            { value: 'DEFECTIVE', label: 'Гэмтэлтэй — карантин' },
            { value: 'RETURN_TO_SUPPLIER', label: 'Нийлүүлэгч рүү буцаах' },
            { value: 'WRITTEN_OFF', label: 'Актлах' },
          ]}
        />
      </Field>
      {/* Бүтэн буцаалт — дүн нь гутлын төлсөн бүтэн дүн; солилцоо «Буцаалт, солилцоо» хуудсанд */}
      <Field label="Мөнгийг буцааж өгөх хэлбэр">
        <Select value={refundMethod} onChange={setRefundMethod} options={REFUND_METHOD_OPTIONS} />
      </Field>
      <p className="text-[12.5px] text-ink-2">
        Буцаах дүн <span className="num font-semibold text-ink">{formatMnt(line.lineTotalMnt)}</span> · бүтэн
      </p>
      <Field label="Тэмдэглэл">
        <Textarea rows={2} value={notes} onChange={(ev) => setNotes(ev.target.value)} />
      </Field>
      {run.error ? <ErrorNote>{(run.error as Error).message}</ErrorNote> : null}
    </Modal>
  );
}

function SumLine({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <dt className="text-ink-2">{label}</dt>
      <dd className="num">{value}</dd>
    </div>
  );
}

/** Захиалгын жагсаалт — 640px-ээс доош захиалга тус бүрийн карт. */
function orderColumns(): Column<OrderRow>[] {
  return [
    { key: 'code', header: 'Дугаар', mobile: 'title', cell: (o) => <span className="num">{o.code}</span> },
    // Салбар, худалдан авагчийн кирилл нэр — боогдоно
    { key: 'store', header: 'Салбар', cell: (o) => <span className="text-ink-2 text-pretty">{o.store.name}</span> },
    {
      key: 'customer',
      header: 'Худалдан авагч',
      cell: (o) => <span className="text-ink-2 text-pretty">{o.customer?.name ?? o.customer?.phone ?? '—'}</span>,
    },
    { key: 'units', header: 'Ширхэг', align: 'right', cell: (o) => formatQty(o._count.lines) },
    { key: 'total', header: 'Дүн', align: 'right', mobile: 'value', cell: (o) => formatMnt(o.totalMnt) },
    {
      key: 'payment',
      header: 'Төлбөр',
      cell: (o) => <span className="text-ink-2">{paymentSummary(o)}</span>,
    },
    {
      key: 'status',
      header: 'Төлөв',
      cell: (o) => (
        <span
          className={`inline-block rounded-full px-2.5 py-[3px] text-[11px] whitespace-nowrap ${
            o.status === 'ACTIVE' ? 'bg-accent-100 text-accent-700' : 'bg-alert-bg text-alert-ink'
          }`}
          title={o.voidReason ?? undefined}
        >
          {o.status === 'ACTIVE' ? 'Идэвхтэй' : 'Хүчингүй'}
        </span>
      ),
    },
    {
      key: 'at',
      header: 'Огноо',
      align: 'right',
      cell: (o) => <span className="text-ink-2">{formatDate(o.soldAt)}</span>,
    },
  ];
}

/** Буцаалт — 640px-ээс доош буцаалт тус бүрийн карт. */
function returnColumns(): Column<ReturnRow>[] {
  return [
    {
      key: 'serial',
      header: 'Серийн дугаар',
      mobile: 'title',
      cell: (r) => (
        <span className="num">
          {r.orderLine ? formatSerialDisplay(r.orderLine.snapshot.serial) : '—'}
        </span>
      ),
    },
    {
      key: 'model',
      header: 'Загвар',
      cell: (r) => <span className="text-ink-2 text-pretty">{r.orderLine?.snapshot.modelName ?? '—'}</span>,
    },
    {
      key: 'type',
      header: 'Төрөл',
      cell: (r) => (
        <span className="flex flex-col">
          <span>{RETURN_TYPE_LABEL[r.type]}</span>
          <span className="num text-label text-ink-3">
            {r.orderLine?.order.code ?? '—'}
            {r.exchangeOrder ? ` → ${r.exchangeOrder.code}` : ''}
          </span>
        </span>
      ),
    },
    { key: 'reason', header: 'Шалтгаан', cell: (r) => <span className="text-ink-2 text-pretty">{r.reason?.labelMn ?? '—'}</span> },
    {
      key: 'disposition',
      header: 'Гутлыг яасан',
      cell: (r) => <span className="text-ink-2 text-pretty">{RETURN_DISPOSITION_LABEL[r.disposition] ?? r.disposition}</span>,
    },
    {
      key: 'refund',
      header: 'Буцаасан мөнгө',
      align: 'right',
      mobile: 'value',
      cell: (r) => (
        <span className="flex flex-col items-end">
          <span>{Number(r.refundMnt) > 0 ? formatMnt(r.refundMnt) : '—'}</span>
          {r.refundMethod ? <span className="text-label text-ink-3">{PAYMENT_LABEL[r.refundMethod] ?? r.refundMethod}</span> : null}
        </span>
      ),
    },
    { key: 'by', header: 'Бүртгэсэн', cell: (r) => <span className="text-ink-2 text-pretty">{r.processedBy?.fullName ?? '—'}</span> },
    {
      key: 'at',
      header: 'Огноо',
      align: 'right',
      cell: (r) => <span className="text-ink-2">{formatDate(r.returnedAt)}</span>,
    },
  ];
}

/* Дэлгэрэнгүйн модал доторх хүснэгт эдгээрийг ашигласаар байна —
   хуудасны жагсаалтууд `DataTable` руу шилжсэн ч энэ хоёр үлдэнэ. */
function Th({ children, num = false }: { children: React.ReactNode; num?: boolean }) {
  return (
    <th
      {...(num ? { 'data-num': 'true' } : {})}
      className={`border-b border-hair px-3 py-2 text-[11.5px] font-medium tracking-wide
        text-ink-2 uppercase whitespace-nowrap ${num ? 'text-right' : 'text-left'}`}
    >
      {children}
    </th>
  );
}

function Td({
  children, num = false, muted = false,
}: {
  children: React.ReactNode; num?: boolean; muted?: boolean;
}) {
  return (
    <td
      {...(num ? { 'data-num': 'true' } : {})}
      className={`border-b border-hair/70 px-3 py-2 ${num ? 'text-right whitespace-nowrap' : ''} ${
        muted ? 'text-ink-2' : ''
      }`}
    >
      {children}
    </td>
  );
}
